/**
 * Front-end behaviour for mea/insights-featured: the carousel's paging arrows
 * and the archive's Load more control.
 *
 * The track is a real horizontal scroller, so the row is already usable with
 * touch, a trackpad and the keyboard before this file runs; all the arrows add
 * is paging by a full row, which is what node 2162:4593 draws them for.
 * Nothing here creates or moves a card - the list is server-rendered.
 *
 * Paging wraps rather than disabling at the ends: the design draws one arrow
 * state, so a disabled state would be an invention.
 *
 * The archive half is the same contract mea/careers-feed works under: render.php
 * puts EVERY published article on the page, and the section is complete and
 * correct with this file absent or blocked. All the script does is hide the rows
 * past the first page and offer a button that brings them back, so nothing here
 * ever creates, fetches or orders a card.
 */

const GAP = 28;

function pageWidth( track ) {
	const card = track.querySelector( '.mea-insights-featured__item' );
	if ( ! card ) {
		return track.clientWidth;
	}

	const styles = window.getComputedStyle( track.querySelector( '.mea-insights-featured__grid' ) );
	const gap = parseFloat( styles.columnGap ) || GAP;
	const pitch = card.getBoundingClientRect().width + gap;
	const perView = Math.max( 1, Math.round( ( track.clientWidth + gap ) / pitch ) );

	return pitch * perView;
}

function scrollBehavior() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
		? 'auto'
		: 'smooth';
}

function page( track, direction ) {
	const step = pageWidth( track );
	const max = track.scrollWidth - track.clientWidth;
	let target = track.scrollLeft + direction * step;

	// Wrap at both ends. The 1px slack absorbs the sub-pixel card widths the
	// design's own arithmetic produces (378.667 and 360).
	if ( target > max + 1 ) {
		target = 0;
	} else if ( target < -1 ) {
		target = max;
	}

	track.scrollTo( { left: Math.max( 0, Math.min( max, target ) ), behavior: scrollBehavior() } );
}

function init( carousel ) {
	const track = carousel.querySelector( '[data-mea-insights-track]' );
	if ( ! track ) {
		return;
	}

	const prev = carousel.querySelector( '[data-mea-insights-prev]' );
	const next = carousel.querySelector( '[data-mea-insights-next]' );

	if ( prev ) {
		prev.addEventListener( 'click', () => page( track, -1 ) );
	}
	if ( next ) {
		next.addEventListener( 'click', () => page( track, 1 ) );
	}
}

/**
 * Fill {token} placeholders in an editable copy string.
 *
 * @param {string} template Copy from a block attribute.
 * @param {Object} values   Token name to replacement value.
 * @return {string} The filled string.
 */
function fill( template, values ) {
	return Object.keys( values ).reduce( function ( text, token ) {
		return text.split( '{' + token + '}' ).join( values[ token ] );
	}, template || '' );
}

/**
 * Wire one archive instance: hide everything past the first page, then reveal
 * one page per click.
 *
 * @param {HTMLElement} actions The `[data-mea-insights-more]` control group,
 *                              which render.php ships `hidden`.
 */
function initArchive( actions ) {
	const section = actions.closest( '.mea-insights-featured' );
	const list = section && section.querySelector( '[data-mea-insights-list]' );
	if ( ! list ) {
		return;
	}

	const items = Array.prototype.slice.call(
		list.querySelectorAll( '.mea-insights-featured__item' )
	);
	const pageSize = Math.max(
		1,
		parseInt( actions.getAttribute( 'data-page-size' ), 10 ) || 9
	);

	// Nothing to narrow: leave the list alone and leave the control hidden, so
	// a dead button is never reachable. Same reasoning as the carousel's inert
	// arrows above.
	if ( items.length <= pageSize ) {
		return;
	}

	const button = actions.querySelector( '[data-mea-insights-load]' );
	const status = actions.querySelector( '[data-mea-insights-status]' );
	let shown = pageSize;

	function apply() {
		items.forEach( function ( item, index ) {
			// The `hidden` attribute rather than a class: it takes the card out
			// of the accessibility tree and the tab order as well as the layout.
			item.hidden = index >= shown;
		} );

		if ( status ) {
			status.textContent = fill( status.getAttribute( 'data-template' ), {
				shown: String( shown ),
				total: String( items.length ),
			} );
		}

		// Everything is out: hide the button rather than disable it, so a
		// control that can no longer do anything is not reachable either. Same
		// reasoning as the carousel's inert arrows above.
		if ( shown >= items.length && button ) {
			button.hidden = true;
		}
	}

	if ( button ) {
		button.addEventListener( 'click', function () {
			const first = shown;
			shown = Math.min( items.length, shown + pageSize );
			apply();

			// Send the reader to the top of what just appeared rather than
			// leaving them where the button used to be.
			const link = items[ first ] && items[ first ].querySelector( 'a' );
			if ( link ) {
				link.focus( { preventScroll: true } );
			}
		} );
	}

	apply();
	actions.hidden = false;
}

function boot() {
	document
		.querySelectorAll( '[data-mea-insights-carousel]' )
		.forEach( init );

	document
		.querySelectorAll( '[data-mea-insights-more]' )
		.forEach( initArchive );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', boot );
} else {
	boot();
}
